import React from 'react';
import { TailwindDateField } from '../../../components/forms/TailwindDateField';

// Three-date timeline field for Admissions settings.
//
// Desktop (sm+): three dots connected by horizontal lines — an arc metaphor
// showing progression from "Opens" → "Closes" → "Decisions".
//
// Mobile (<sm): stacked labeled date inputs with dot indicators.

interface TimelineFieldProps {
  openDate: string;
  closeDate: string;
  decisionDate: string;
  onOpenDateChange: (value: string) => void;
  onCloseDateChange: (value: string) => void;
  onDecisionDateChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  testId?: string;
}

const DATE_INPUT_CLASS =
  'w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-sm h-9 px-2 transition-colors border disabled:opacity-50 disabled:cursor-not-allowed';

// --- Desktop dot node ---
interface DotNodeProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  dotClass: string;
}

const DotNode: React.FC<DotNodeProps> = ({
  label,
  value,
  onChange,
  onBlur,
  disabled,
  dotClass,
}) => (
  <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
    <div
      className={`w-3 h-3 rounded-full border-2 ${dotClass} shrink-0`}
      aria-hidden="true"
    />
    <div className="w-full text-center">
      <p className="text-xs font-medium text-text-secondary mb-1.5">{label}</p>
      <TailwindDateField
        value={value}
        onValueChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        className={DATE_INPUT_CLASS}
        centered
        aria-label={label}
      />
    </div>
  </div>
);

// --- Mobile stacked row ---
interface StackedRowProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  dotClass: string;
}

const StackedRow: React.FC<StackedRowProps> = ({
  label,
  value,
  onChange,
  onBlur,
  disabled,
  dotClass,
}) => (
  <div>
    <label className="flex items-center gap-2 text-xs font-medium text-text-secondary mb-1.5">
      <span
        className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotClass}`}
        aria-hidden="true"
      />
      {label}
    </label>
    <TailwindDateField
      value={value}
      onValueChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      className={DATE_INPUT_CLASS}
      aria-label={label}
    />
  </div>
);

// Connector line between desktop dots
const Connector: React.FC = () => (
  <div className="flex items-start pt-[22px] w-6 shrink-0" aria-hidden="true">
    <div className="h-px w-full bg-border" />
  </div>
);

export const TimelineField: React.FC<TimelineFieldProps> = ({
  openDate,
  closeDate,
  decisionDate,
  onOpenDateChange,
  onCloseDateChange,
  onDecisionDateChange,
  onBlur,
  disabled,
  className = '',
  testId,
}) => (
  <div data-testid={testId} className={className}>
    {/* Desktop: three-dot horizontal arc */}
    <div className="hidden sm:flex items-start">
      <DotNode
        label="Opens"
        value={openDate}
        onChange={onOpenDateChange}
        onBlur={onBlur}
        disabled={disabled}
        dotClass="border-aquamarine bg-aquamarine"
      />
      <Connector />
      <DotNode
        label="Closes"
        value={closeDate}
        onChange={onCloseDateChange}
        onBlur={onBlur}
        disabled={disabled}
        dotClass="border-primary bg-primary"
      />
      <Connector />
      <DotNode
        label="Decisions"
        value={decisionDate}
        onChange={onDecisionDateChange}
        onBlur={onBlur}
        disabled={disabled}
        dotClass="border-coral bg-coral"
      />
    </div>

    {/* Mobile: stacked labeled date fields */}
    <div className="sm:hidden flex flex-col gap-3">
      <StackedRow
        label="Opens"
        value={openDate}
        onChange={onOpenDateChange}
        onBlur={onBlur}
        disabled={disabled}
        dotClass="bg-aquamarine"
      />
      <StackedRow
        label="Closes"
        value={closeDate}
        onChange={onCloseDateChange}
        onBlur={onBlur}
        disabled={disabled}
        dotClass="bg-primary"
      />
      <StackedRow
        label="Decisions"
        value={decisionDate}
        onChange={onDecisionDateChange}
        onBlur={onBlur}
        disabled={disabled}
        dotClass="bg-coral"
      />
    </div>
  </div>
);
